'use client';

/**
 * UzForm + UzFormField - RHF + Zod integration for the Uz layer.
 *
 * Usage:
 *
 *   const form = useForm<MySchema>({ resolver: zodResolver(MySchema) });
 *
 *   return (
 *     <UzForm form={form} onSubmit={form.handleSubmit(submit)}>
 *       <UzFormField name="email" label="Email">
 *         {({ field, error }) => (
 *           <UzInput {...field} error={error} required />
 *         )}
 *       </UzFormField>
 *       <UzButton type="submit" loading={form.formState.isSubmitting}>Save</UzButton>
 *     </UzForm>
 *   );
 *
 * UzFormField is intentionally NOT a 1:1 wrap of shadcn's <FormField>. It uses
 * a render-prop signature that gives the consumer direct access to the field
 * state and the resolved error message. That lets us pass error/required to
 * any Uz control without each control re-implementing form glue.
 */

import {
  Controller,
  FormProvider,
  useFormContext,
  type ControllerRenderProps,
  type FieldPath,
  type FieldValues,
  type UseFormReturn,
} from 'react-hook-form';

import { cn } from '../../utils/cn';

import type { FormHTMLAttributes, ReactNode } from 'react';

export interface UzFormProps<TValues extends FieldValues> extends Omit<
  FormHTMLAttributes<HTMLFormElement>,
  'onSubmit'
> {
  form: UseFormReturn<TValues>;
  onSubmit?: FormHTMLAttributes<HTMLFormElement>['onSubmit'];
  children: ReactNode;
}

export const UzForm = <TValues extends FieldValues>({
  form,
  onSubmit,
  children,
  className,
  ...rest
}: UzFormProps<TValues>): React.JSX.Element => (
  <FormProvider {...form}>
    <form onSubmit={onSubmit} noValidate className={cn('space-y-4', className)} {...rest}>
      {children}
    </form>
  </FormProvider>
);

export interface UzFormFieldRenderArgs<TValue> {
  field: Omit<ControllerRenderProps, 'value' | 'onChange'> & {
    value: TValue;
    onChange: (value: TValue) => void;
  };
  error?: string;
  invalid: boolean;
  isDirty: boolean;
  isTouched: boolean;
}

export interface UzFormFieldProps<TValues extends FieldValues, TName extends FieldPath<TValues>> {
  name: TName;
  /** Render-prop receiving field state + resolved error message. */
  children: (args: UzFormFieldRenderArgs<TValues[TName]>) => ReactNode;
}

export const UzFormField = <TValues extends FieldValues, TName extends FieldPath<TValues>>({
  name,
  children,
}: UzFormFieldProps<TValues, TName>): React.JSX.Element => {
  const { control } = useFormContext<TValues>();
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) =>
        children({
          field: field as never,
          error: fieldState.error?.message,
          invalid: fieldState.invalid,
          isDirty: fieldState.isDirty,
          isTouched: fieldState.isTouched,
        }) as React.ReactElement
      }
    />
  );
};
